import Core               from "@/core/Core";
import BankAccount        from "./BankAccount";
import BankAccountsDAO    from "./BankAccountsDAO";
import Partner            from "@/domains/Partners/Partner";
import Company            from "@/domains/Companies/Company";
import Country            from "@/domains/Countries/Country";
import ContextMenuItem    from "@/lib/components/VContextMenu/ContextMenuItem";

/**
 * Сервис для работы с расчётными счетами
 */
class BankAccountsService {

  static async get(id: number): Promise<BankAccount | null> {
    const response = await BankAccountsDAO.get(id);

    return Core.utils.hydrate(response.data, BankAccount.fromServer);
  }

  static async getList(companyId: number): Promise<BankAccount[]> {
    const response = await BankAccountsDAO.getList(companyId);

    return Core.utils.hydrateArray(response.data, BankAccount.fromServer);
  }

  /** ------- CRUD ------- */

  static async add(companyId: number, bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.add(companyId, bankAccount);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт успешно добавлен');
    }

    return response.ok;
  }

  static async delete(bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.delete(bankAccount.id);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт успешно удалён');
    }

    return response.ok;
  }

  static async setMain(bankAccount: BankAccount): Promise<boolean> {
    const response = await BankAccountsDAO.setMain(bankAccount.id);

    if (response.ok) {
      Core.showNotification.success('Расчётный счёт установлен как основной');
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню счёта.
   * Возвращает true если удаление произошло — грид сам обновится по refreshAfter.
   */
  static async deleteWithConfirmation(bankAccount: BankAccount): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этот расчётный счёт?',
      Core.utils.regional.ru.formatBankAccount(bankAccount.account),
    );

    if (!confirmed) {
      return false;
    }

    return await this.delete(bankAccount);
  }

  /** ------ открытие диалога создания/просмотра расчётного счёта ------ */

  static async createBankAccount(
    partner: Partner,
    localCompany: Company | null,
    onCompanyCreated: (data?: any) => void,
  ) {
    const newBankAccount = BankAccount.fromPartner(partner);

    if (localCompany) {
      newBankAccount.company = localCompany;
    }

    this.showBankAccountDialog(newBankAccount, partner, onCompanyCreated, null);
  }

  /** на самом деле не update, а readonly просмотр */
  static async updateBankAccount(
    partner: Partner,
    bankAccount: BankAccount,
    companies: Company[],
    onCompanyUpdated: (data?: any) => void,
  ) {
    /** компания с сервера содержит только id, нужен хотя бы ещё name для отображения при открытии */
    const fullCompany = companies.find(company => company.id === bankAccount.company.id);

    if (fullCompany) {
      bankAccount.company = fullCompany;
    }

    /** назначаем валюту как у партнёра */
    if (bankAccount.company) {
      bankAccount.company.country = { code: partner.country.code } as Country;
    }

    const bankAccountFromServer = BankAccount.fromServer(bankAccount);

    this.showBankAccountDialog(bankAccountFromServer, partner, null, onCompanyUpdated);
  }

  static showBankAccountDialog(
    bankAccount: BankAccount,
    partner: Partner,
    onCompanyCreated: (data?: any) => void,
    onCompanyUpdated: (data?: any) => void,
  ) {
    Core.dialogs.BankAccountCard.show(
      {
        bankAccount,
        partner,
      },
      {
        [BankAccount.Events.Created]: onCompanyCreated,
        [BankAccount.Events.Updated]: onCompanyUpdated,
      }
    );
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Контекстное меню счёта. Фабрика, а не геттер: пункты меняют данные на беке,
   * а грид секции питается статичным :data из localPartner.companies — сам он
   * перечитать счета не может. Поэтому колбэк onChange замыкается здесь и
   * дёргается после действия: секция эмитит наверх, PartnerCard перечитывает
   * партнёра, грид перерисовывается через :data.
   *
   * @param onChange колбэк «данные изменились» — обновление приходит сверху
   *                 через перечитывание партнёра.
   */
  static getContextMenu(onChange: () => void): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Сделать основным',
        'verified',
        async (bankAccount: BankAccount) => {
          if (await BankAccountsService.setMain(bankAccount)) {
            onChange();
          }
        },
        (bankAccount: BankAccount) => bankAccount.getReasonCannotSetMain(),
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        async (bankAccount: BankAccount) => {
          if (await BankAccountsService.deleteWithConfirmation(bankAccount)) {
            onChange();
          }
        },
      ),
    ];
  }
}

export default BankAccountsService;
